import {
  put,
  select,
  take,
  takeLatest,
  race,
  delay,
} from 'typed-redux-saga/macro';

import { selectConfigEngines } from '../engines/selectors';
import { FETCH_ORGANIZATION_TDOS_SUCCESS } from '@redact-modules/mainPage/actions';
import {
  selectCurrentTdoId,
  selectDetectionRunning,
  selectGlobalSettings,
  selectIsImage,
  selectMediaDuration,
  selectTdo,
  FETCH_TDO_SUCCESS,
  selectClusterList,
  selectUdrAsset,
  selectSelectedBoundingPolyGroup,
  selectTranscriptionView,
  selectDetectObjects,
  selectClusterMap,
  selectBackgroundBlurEdits,
  actionReplaceSelectedGroups,
  actionReplaceClusterMap,
  actionPurgeDetections,
  workerClearUndoRedoAction,
} from '@common-modules/mediaDetails';
import {
  IN_LOAD_DETECTION_DATA,
  IN_SEND_TO_HEAD_DETECTION,
  OUT_DETECTION_CLUSTER_GROUPS,
  OUT_BOUNDING_POLY_COLLECTION,
  OUT_SELECTED_BOUNDINGPOLY_GROUP,
  ClusterMapItem,
  OUT_CLUSTER_MAP,
} from '@worker';

import { ROUTE_HOME } from '../routing';
import {
  CREATE_JOB_FACES_ACTION,
  CREATE_JOB_FACES_SUCCESS,
  CreateJobFacesActionType,
  DELETE_OBJECT_DETECTION_RESULTS,
  DELETE_OBJECT_DETECTION_RESULTS_FAILURE,
  DELETE_OBJECT_DETECTION_RESULTS_SUCCESS,
} from './index';
import { selectFeatureFlags } from '@user-permissions';
import { ClusterItem, DetailTDO } from '@common-modules/mediaDetails/models';
import { deleteAllHeadDetectionResults } from '@common-modules/mediaDetails/services';
import { saveMediaData } from '@worker/services';

export function* watchCreateJobFacesData() {
  yield* takeLatest(CREATE_JOB_FACES_ACTION, watchCreateJobFacesDataHandle);
}

export function* watchDeleteDetectionResults() {
  yield* takeLatest(
    DELETE_OBJECT_DETECTION_RESULTS,
    watchDeleteDetectionResultsHandle
  );
}

/**
 * Clears existing detection data (clusters, polys) from the store,
 * preserving UDRs and other edits, and saves the result.
 */
function* purgeDetectionsAndSaveSaga(tdoId: string) {
  const clusterList = yield* select(selectClusterList);

  let clusterMap = yield* select(selectClusterMap);
  clusterMap = purgeDetectionGroupsFromClusterMap(clusterMap);

  const selectedBoundingPolys = yield* select(selectSelectedBoundingPolyGroup);
  const selectedPolyGroups = clearSelectedDetections(
    clusterList,
    selectedBoundingPolys
  );

  const { boundingPolys } = yield* select(selectUdrAsset);
  const { audioRedactionEdits, voiceMaskEdits } = yield* select(
    selectTranscriptionView
  );

  const backgroundBlurEdits = yield* select(selectBackgroundBlurEdits);

  // someday might make sense to refactor to have this saga run in the web-worker instead - that way don't need action to replace data on both web-worker and main-thread

  /* Remove existing detections */
  yield* put(actionPurgeDetections()); // clear detections on the web-worker

  yield* put(OUT_BOUNDING_POLY_COLLECTION({})); // replace detection collections on main thread
  yield* put(OUT_DETECTION_CLUSTER_GROUPS({})); // clear the current detection cluster groups - main thread

  yield* put(OUT_CLUSTER_MAP(clusterMap)); // replace clusterMap on main thread
  yield* put(actionReplaceClusterMap(clusterMap)); // replace clusterMap on web-worker

  yield* put(OUT_SELECTED_BOUNDINGPOLY_GROUP({ selected: selectedPolyGroups })); // replaces selected in the main thread
  yield* put(actionReplaceSelectedGroups({ selected: selectedPolyGroups })); // replace selected in the web-worker

  yield* put(
    saveMediaData({
      tdoId: tdoId,
      selectedPolyGroups,
      boundingPolys,
      clusterMap,
      audioRedactions: audioRedactionEdits,
      voiceMaskEdits,
      backgroundBlurEdits,
    })
  );

  yield* put(workerClearUndoRedoAction()); // clear undo/redo history both web-worker and main thread
}

export function* watchDeleteDetectionResultsHandle({
  payload: { onlyUserEdited },
}: {
  payload: {
    onlyUserEdited?: boolean;
  };
}) {
  const tdo: DetailTDO | null = yield* select(selectTdo);

  if (tdo) {
    yield* purgeDetectionsAndSaveSaga(tdo.id);

    yield* put(deleteAllHeadDetectionResults({ onlyUserEdited }));

    const { success } = yield* race({
      success: take(DELETE_OBJECT_DETECTION_RESULTS_SUCCESS),
      failure: take(DELETE_OBJECT_DETECTION_RESULTS_FAILURE),
    });

    // if only user edited reload the original results
    if (success && onlyUserEdited) {
      const tdoId = yield* select(selectCurrentTdoId);
      const { detectionEngineIds, detectionCategory } =
        yield* select(selectConfigEngines);
      const mediaDuration = yield* select(selectMediaDuration);
      if (tdoId) {
        yield* put(
          IN_LOAD_DETECTION_DATA({
            tdoId,
            engines: detectionEngineIds,
            mediaDuration,
            categoryId: detectionCategory,
          })
        );
      }
    }
  }
}

const purgeDetectionGroupsFromClusterMap = (clusterMap: {
  readonly [clusterId: string]: ClusterMapItem;
}) => {
  const filterClusterMap: {
    [clusterId: string]: ClusterMapItem;
  } = {};

  for (const [clusterId, clusterMapItem] of Object.entries(clusterMap)) {
    // only keep clusters that have udr groups
    if (clusterMapItem.udrGroupIds.length > 0) {
      filterClusterMap[clusterId] = {
        ...clusterMapItem,
        detectionGroupIds: [], // clear any detectionGroupIds
      };
    }
  }
  return filterClusterMap;
};

export function* watchCreateJobFacesDataHandle({
  payload,
}: {
  payload?: CreateJobFacesActionType;
}) {
  const tdo: DetailTDO | null = yield* select(selectTdo);

  const {
    videoType: payloadVideoType,
    faceDetectionThreshold: payloadThreshold,
  } = payload || {};

  if (tdo) {
    const isImage = yield* select(selectIsImage);
    const {
      apiRoot,
      defaultClusterId,
      detectionEngineId,
      fastChunkerEngineId,
      outputWriterEngineId,
      glcIngestionEngineId,
    } = yield* select(selectConfigEngines);
    const {
      faceDetectionThreshold,
      videoType,
      detectionRate,
      legacyClustering,
      clusterSimThreshold,
    } = yield* select(selectGlobalSettings);
    const featureFlags: Record<string, any> = yield* select(selectFeatureFlags);
    const detectObjects = yield* select(selectDetectObjects);
    const req = {
      apiRoot,
      tdoId: tdo.id,
      tdoName: tdo.name,
      clusterId: defaultClusterId,
      detectionEngineId,
      glcIngestionEngineId,
      fastChunkerEngineId,
      outputWriterEngineId,
      confidenceThreshold:
        (payloadThreshold ?? (faceDetectionThreshold || 60)) / 100,
      videoType: payloadVideoType ?? (videoType || 'Bodycam'),
      detectionRate: detectionRate || 1,
      legacyClustering,
      clusterSimThreshold,
      featureFlags,
      detectObjects,
      isImage,
    };

    yield* purgeDetectionsAndSaveSaga(tdo.id);

    yield* put(deleteAllHeadDetectionResults({}));

    yield* put(IN_SEND_TO_HEAD_DETECTION(req));
  }
}

export function* watchCreateJobFacesSuccessHandle() {
  yield* take(FETCH_ORGANIZATION_TDOS_SUCCESS);
  // LTODO what is the purpose of this function? Might be legacy code... loading is done in web-worker now
  const tdoId = yield* select(selectCurrentTdoId);
  if (!tdoId) {
    return;
  }
  const tdo = yield* select(selectTdo);
  const trimmedVideo = Boolean(
    tdo?.details.veritoneProgram?.sourceType === 'trimmed'
  );
  const { detectionEngineIds } = yield* select(selectConfigEngines);
  const mediaDuration = yield* select(selectMediaDuration);

  let hadDetectionRunning = false;
  while (true) {
    const { ok } = yield* race({
      ok: delay(1000),
      __: take(ROUTE_HOME),
    });
    if (!ok) {
      return;
    }

    const tasks = yield* select(selectDetectionRunning);
    if (tasks.length === 0) {
      break;
    }
    hadDetectionRunning = true;
  }

  if (hadDetectionRunning || trimmedVideo) {
    // LTODO why are we trying to load when object detection is still running???
    const { detectionCategory } = yield* select(selectConfigEngines);
    yield* put(
      IN_LOAD_DETECTION_DATA({
        tdoId,
        engines: detectionEngineIds,
        mediaDuration,
        categoryId: detectionCategory,
      })
    );
  }
}

export function* watchCreateJobFacesSuccess() {
  yield* takeLatest(
    [CREATE_JOB_FACES_SUCCESS, FETCH_TDO_SUCCESS],
    watchCreateJobFacesSuccessHandle
  );
}

const clearSelectedDetections = (
  clusterList: ReadonlyArray<ClusterItem>,
  selectedPolyGroups: { readonly [id: string]: boolean | undefined }
) =>
  clusterList.reduce<Record<string, boolean | undefined>>(
    (prev, { groups }) => ({
      ...prev,
      ...groups.reduce<Record<string, boolean | undefined>>(
        (groupPrev, group) => ({
          ...groupPrev,
          ...group.segments.reduce<Record<string, boolean | undefined>>(
            (segmentPrev, segment) => ({
              ...segmentPrev,
              ...segment.subsegmentIds.reduce<
                Record<string, boolean | undefined>
              >(
                (subsegmentIds, subsegmentId) => ({
                  ...subsegmentIds,
                  ...(segment.type === 'udr' && {
                    [subsegmentId]: selectedPolyGroups[subsegmentId], // keep udrs
                  }),
                }),
                {}
              ),
              // ...(segment.type === 'udr' && { [segment.id]: selectedPolyGroups[id] }), // we dont need segment level ids anymore
            }),
            {}
          ),
        }),
        {}
      ),
      // ...(type === 'udr' && { [id]: selectedPolyGroups[id] }), // we dont need cluster level ids anymore
    }),
    {}
  );
